import { motion } from "framer-motion";
import { Play, Users, Bot, RotateCcw, PlayCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const GAME_MODES = [
  { id: "classic", name: "X O X", icon: "\u274C\u2B55", color: "#FF6B6B", gradient: "from-red-400 to-pink-500", desc: "Dapatkan 3 berturut untuk menang!" },
  { id: "gomoku", name: "Gomoku", icon: "\u26AB\u26AA", color: "#A855F7", gradient: "from-purple-400 to-indigo-500", desc: "Dapatkan 5 berturut untuk menang!" },
  { id: "sos", name: "SOS", icon: "\uD83C\uDD98", color: "#F97316", gradient: "from-orange-400 to-amber-500", desc: "Bentuk pola S-O-S untuk dapat poin!" },
  { id: "memory", name: "Memory Card", icon: "\uD83C\uDFB4", color: "#06B6D4", gradient: "from-cyan-400 to-teal-500", desc: "Cocokkan 2 kartu yang sama!" },
  { id: "maze", name: "Labirin Ajaib", icon: "\uD83C\uDF1F", color: "#10B981", gradient: "from-emerald-400 to-green-500", desc: "Jelajahi labirin, temukan jawaban!" },
  { id: "whack", name: "Pukul Jawaban!", icon: "\uD83D\uDCA5", color: "#EAB308", gradient: "from-yellow-400 to-amber-500", desc: "Pukul jawaban benar sebelum hilang!" },
  { id: "snake", name: "Ular Kata Ceria", icon: "\uD83D\uDC0D", color: "#4ade80", gradient: "from-green-400 to-emerald-500", desc: "Arahkan ular ke huruf yang benar!" },
  { id: "block", name: "Balok Bentuk Pintar", icon: "\uD83E\uDDE9", color: "#3B82F6", gradient: "from-blue-400 to-indigo-500", desc: "Cocokkan bentuk, warna, dan pola!" },
  { id: "simon", name: "Ikuti Lampu Ajaib", icon: "\uD83D\uDCA1", color: "#F472B6", gradient: "from-pink-400 to-rose-500", desc: "Ingat dan ulangi pola lampu!" },
  { id: "snakeladder", name: "Ular Tangga Pintar", icon: "\uD83C\uDFB2", color: "#F59E0B", gradient: "from-yellow-400 to-orange-500", desc: "Lempar dadu & jawab pertanyaan!" },
  { id: "balon", name: "Balon Teman", icon: "\uD83C\uDF88", color: "#87CEEB", gradient: "from-sky-400 to-blue-500", desc: "Kerja sama jaga balon melayang!" },
  { id: "dongbola", name: "Dorong Bola ke Rumah", icon: "\u26BD", color: "#FF6347", gradient: "from-orange-400 to-red-500", desc: "Dorong bola bersama ke rumah!" },
  { id: "masak", name: "Masak Bareng", icon: "\uD83C\uDF73", color: "#FF9800", gradient: "from-amber-400 to-orange-500", desc: "Masak makanan bersama teman!" },
  { id: "tebak", name: "Tebak Gambar", icon: "\uD83D\uDD0D", color: "#9B59B6", gradient: "from-purple-400 to-pink-500", desc: "Tebak gambar yang blur!" },
  { id: "menara", name: "Menara Balok", icon: "\uD83C\uDFD7\uFE0F", color: "#E74C3C", gradient: "from-red-400 to-orange-500", desc: "Susun balok setinggi mungkin!" },
  { id: "tangkap", name: "Tangkap Bintang", icon: "\u2B50", color: "#F1C40F", gradient: "from-yellow-400 to-amber-500", desc: "Tangkap bintang jatuh!" },
  { id: "puzzle", name: "Puzzle Potong", icon: "\uD83E\uDDE9", color: "#3498DB", gradient: "from-blue-400 to-cyan-500", desc: "Susun potongan gambar!" },
  { id: "lomba", name: "Lomba Lari", icon: "\uD83C\uDFC3", color: "#2ECC71", gradient: "from-green-400 to-emerald-500", desc: "Lomba lari hewan!" },
  { id: "mewarnai", name: "Mewarnai", icon: "\uD83C\uDFA8", color: "#FF69B4", gradient: "from-pink-400 to-rose-500", desc: "Warnai gambar dengan kuas!" },
  { id: "hitung", name: "Game Hitung", icon: "\uD83E\uDDEE", color: "#3498DB", gradient: "from-blue-400 to-cyan-500", desc: "Belajar matematika dengan seru!" },
  { id: "musik", name: "Game Musik", icon: "\uD83C\uDFB5", color: "#9B59B6", gradient: "from-purple-400 to-pink-500", desc: "Mainkan piano, drum & xylophone!" },
  { id: "balap", name: "Game Balap", icon: "\uD83C\uDFCE\uFE0F", color: "#E74C3C", gradient: "from-red-400 to-orange-500", desc: "Balapan mobil 2-4 pemain!" },
  { id: "lempar", name: "Lempar Bola", icon: "\uD83C\uDFAF", color: "#1ABC9C", gradient: "from-teal-400 to-green-500", desc: "Lempar bola ke target!" },
  { id: "suara", name: "Tebak Suara", icon: "\uD83D\uDD0A", color: "#F39C12", gradient: "from-amber-400 to-yellow-500", desc: "Tebak suara hewan!" },
  { id: "warna", name: "Cocok Warna", icon: "\uD83C\uDF08", color: "#E91E63", gradient: "from-pink-400 to-fuchsia-500", desc: "Cocokkan warna yang tepat!" },
  { id: "huruf", name: "Belajar Huruf", icon: "\uD83D\uDD24", color: "#FF5722", gradient: "from-orange-400 to-red-500", desc: "Belajar A-Z dengan kata!" },
  { id: "haribulan", name: "Hari & Bulan", icon: "\uD83D\uDCC5", color: "#673AB7", gradient: "from-violet-400 to-purple-500", desc: "Belajar hari & bulan!" },
  { id: "bendera", name: "Tebak Bendera", icon: "\uD83C\uDF0D", color: "#2196F3", gradient: "from-sky-400 to-blue-500", desc: "Tebak negara dari bendera!" },
  { id: "sudoku", name: "Sudoku Anak", icon: "\uD83E\uDDE9", color: "#009688", gradient: "from-teal-400 to-cyan-500", desc: "Sudoku 4×4 angka 1-4!" },
  { id: "pola", name: "Tebak Pola", icon: "\uD83D\uDD22", color: "#FFC107", gradient: "from-yellow-300 to-orange-400", desc: "Lanjutkan pola angka!" },
  { id: "jam", name: "Belajar Jam", icon: "\u23F0", color: "#795548", gradient: "from-amber-600 to-yellow-700", desc: "Belajar membaca jam analog!" },
  { id: "cerita", name: "Cerita Pilih", icon: "\uD83D\uDCD6", color: "#8B5CF6", gradient: "from-violet-500 to-purple-600", desc: "Cerita rakyat dengan pilihan!" },
  { id: "provinsi", name: "Provinsi", icon: "\uD83C\uDDEE\uD83C\uDDE9", color: "#EF4444", gradient: "from-red-500 to-rose-600", desc: "Tebak 34 provinsi Indonesia!" },
  { id: "pahlawan", name: "Pahlawan", icon: "\uD83E\uDDB8", color: "#0EA5E9", gradient: "from-sky-500 to-blue-600", desc: "Kenali pahlawan nasional!" },
];

export const GameSettings = ({
  mode,
  setMode,
  playerType,
  setPlayerType,
  aiDifficulty,
  setAiDifficulty,
  boardSize,
  setBoardSize,
  onStartGame,
  hasSavedGame,
  onContinueGame,
  onResetScores,
  onOpenGame,
  sosPlayerCount = 2,
  setSosPlayerCount,
}) => {
  const isBoardGame = ["classic", "gomoku", "sos"].includes(mode);
  const currentGame = GAME_MODES.find(g => g.id === mode) || GAME_MODES[0];

  const getBoardSizeOptions = () => {
    if (mode === "classic") return [{ value: 3, label: "3x3" }];
    if (mode === "gomoku") return [
      { value: 9, label: "9x9" }, { value: 11, label: "11x11" }, { value: 13, label: "13x13" }
    ];
    if (mode === "sos") return [
      { value: 3, label: "3x3" }, { value: 5, label: "5x5" }, { value: 7, label: "7x7" },
      { value: 9, label: "9x9" }, { value: 11, label: "11x11" },
    ];
    return [];
  };

  const boardSizeOptions = getBoardSizeOptions();

  const handleModeSelect = (modeId) => {
    if (["classic", "gomoku", "sos"].includes(modeId)) {
      setMode(modeId);
      if (modeId === "classic") setBoardSize(3);
      else if (modeId === "gomoku") setBoardSize(9);
      else if (modeId === "sos") setBoardSize(5);
      return;
    }
    onOpenGame?.(modeId);
  };

  return (
    <motion.div
      initial={{ y: 20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5, delay: 0.2 }}
      className="glass-card-strong rounded-2xl p-4 sm:p-6 w-full max-w-3xl mx-auto"
    >
      <h2 className="font-fredoka text-xl sm:text-2xl text-white mb-4 text-center font-bold tracking-wide">
        Pilih Game
      </h2>

      {/* Continue Saved Game */}
      {hasSavedGame && (
        <div className="mb-4">
          <Button data-testid="continue-game-button" onClick={onContinueGame}
            className="w-full bg-gradient-to-r from-orange-400 to-amber-500 text-white hover:from-orange-500 hover:to-amber-600 font-fredoka font-bold uppercase tracking-wide mb-2 rounded-xl h-12 text-base shadow-lg">
            <PlayCircle className="w-5 h-5 mr-2" />Lanjutkan Game
          </Button>
        </div>
      )}

      {/* Game Mode Cards */}
      <div className="mb-4">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 scene-3d">
          {GAME_MODES.map((game, i) => {
            const isActive = mode === game.id;
            return (
              <motion.div key={game.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06, type: "spring", stiffness: 200 }}
              >
                <button
                  data-testid={`mode-select-${game.id}`}
                  onClick={() => handleModeSelect(game.id)}
                  className={`card-3d game-card w-full flex flex-col items-center py-3 px-2 rounded-xl border-2 ${
                    isActive
                      ? `bg-gradient-to-br ${game.gradient} border-white/50 glow-3d`
                      : "glass-card border-gold/10 hover:border-gold/30"
                  }`}
                >
                  <motion.span className="text-3xl sm:text-4xl mb-1 block"
                    animate={isActive ? { scale: [1, 1.2, 1], rotate: [0, 10, -10, 0] } : { y: [0, -3, 0] }}
                    transition={{ duration: isActive ? 0.5 : 2, repeat: Infinity, delay: i * 0.3 }}
                  >{game.icon}</motion.span>
                  <span className={`text-xs sm:text-sm font-fredoka font-bold leading-tight ${isActive ? 'text-white' : 'text-gold-light'}`}>
                    {game.name}
                  </span>
                </button>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Description */}
      <motion.div key={mode}
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-4 p-3 rounded-xl glass-card relative overflow-hidden"
      >
        <p className="font-fredoka text-sm text-gold-light text-center relative z-10">
          <span className="text-lg mr-1">{currentGame.icon}</span>
          <span className="font-bold text-white">{currentGame.name}</span> - {currentGame.desc}
        </p>
      </motion.div>

      {/* Board Game Settings */}
      {isBoardGame && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="space-y-3"
        >
          {/* Player Type */}
          <div className="mb-3">
            <label className="font-fredoka text-gold-light text-sm mb-1.5 block font-bold">Lawan</label>
            <div className="flex gap-2">
              {mode === "sos" ? (
                <>
                  <Button data-testid="player-type-ai"
                    variant={playerType === "ai" ? "default" : "outline"}
                    onClick={() => { setPlayerType("ai"); setSosPlayerCount?.(2); }}
                    className={`flex-1 font-fredoka font-bold rounded-xl h-11 ${playerType === "ai" ? "bg-gradient-to-r from-violet-500 to-purple-600 text-white border-0 shadow-lg" : "glass-card border-gold/20 text-gold hover:bg-gold/10"}`}>
                    <Bot className="w-4 h-4 mr-2" />vs AI
                  </Button>
                  {[2, 3, 4].map(n => (
                    <Button key={n} data-testid={`sos-player-count-${n}`}
                      variant={playerType === "pvp" && sosPlayerCount === n ? "default" : "outline"}
                      onClick={() => { setPlayerType("pvp"); setSosPlayerCount?.(n); }}
                      className={`flex-1 font-fredoka font-bold rounded-xl h-11 ${playerType === "pvp" && sosPlayerCount === n ? "bg-gradient-to-r from-pink-500 to-rose-600 text-white border-0 shadow-lg" : "glass-card border-gold/20 text-gold hover:bg-gold/10"}`}>
                      <Users className="w-4 h-4 mr-1" />{n}P
                    </Button>
                  ))}
                </>
              ) : (
                <>
                  <Button data-testid="player-type-ai"
                    variant={playerType === "ai" ? "default" : "outline"}
                    onClick={() => setPlayerType("ai")}
                    className={`flex-1 font-fredoka font-bold rounded-xl h-11 ${playerType === "ai" ? "bg-gradient-to-r from-violet-500 to-purple-600 text-white border-0 shadow-lg" : "glass-card border-gold/20 text-gold hover:bg-gold/10"}`}>
                    <Bot className="w-4 h-4 mr-2" />vs AI
                  </Button>
                  <Button data-testid="player-type-pvp"
                    variant={playerType === "pvp" ? "default" : "outline"}
                    onClick={() => setPlayerType("pvp")}
                    className={`flex-1 font-fredoka font-bold rounded-xl h-11 ${playerType === "pvp" ? "bg-gradient-to-r from-pink-500 to-rose-600 text-white border-0 shadow-lg" : "glass-card border-gold/20 text-gold hover:bg-gold/10"}`}>
                    <Users className="w-4 h-4 mr-2" />2 Pemain
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* AI Difficulty */}
          {playerType === "ai" && (
            <div className="mb-3">
              <label className="font-fredoka text-gold-light text-sm mb-1.5 block font-bold">Tingkat Kesulitan</label>
              <Select value={aiDifficulty} onValueChange={setAiDifficulty}>
                <SelectTrigger className="glass-card border-gold/20 text-white font-fredoka rounded-xl h-11" data-testid="ai-difficulty-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="glass-card-strong border-gold/20 rounded-xl">
                  <SelectItem value="easy" className="font-fredoka text-white">Mudah</SelectItem>
                  <SelectItem value="medium" className="font-fredoka text-white">Sedang</SelectItem>
                  <SelectItem value="hard" className="font-fredoka text-white">Sulit</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Board Size */}
          {boardSizeOptions.length > 1 && (
            <div className="mb-3">
              <label className="font-fredoka text-gold-light text-sm mb-1.5 block font-bold">Ukuran Papan</label>
              <Select value={boardSize.toString()} onValueChange={(v) => setBoardSize(parseInt(v))}>
                <SelectTrigger className="glass-card border-gold/20 text-white font-fredoka rounded-xl h-11" data-testid="board-size-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="glass-card-strong border-gold/20 rounded-xl">
                  {boardSizeOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value.toString()} className="font-fredoka text-white">{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Start Button */}
          <Button data-testid="start-game-button" onClick={onStartGame}
            className={`w-full bg-gradient-to-r ${currentGame.gradient} text-gold-light hover:opacity-90 font-fredoka font-bold uppercase tracking-wider text-base rounded-xl h-14 shadow-xl`}>
            <Play className="w-5 h-5 mr-2" />
            {playerType === "pvp" ? `Masukkan Nama ${mode === "sos" ? sosPlayerCount : 2} Pemain & Mulai` : "MULAI GAME"}
          </Button>

          {/* Reset */}
          <Button data-testid="reset-scores-button" variant="outline" onClick={onResetScores}
            className="w-full glass-card border-gold/20 text-gold-light/80 hover:text-gold hover:bg-gold/10 font-fredoka rounded-xl h-10">
            <RotateCcw className="w-4 h-4 mr-2" />Reset Skor
          </Button>
        </motion.div>
      )}
    </motion.div>
  );
};

export default GameSettings;
